import { apiClient } from '@/lib/api';

export interface CalibrationRequest {
  testIds: string[];
  channelId?: number;
}

export interface MetricStats {
  mean: number;
  stdDev: number;
  min: number;
  max: number;
  rawValues: number[];
}

export interface ThresholdRecommendation {
  curveRadiusMin: number;
  curveRadiusMax: number;
  maxImaginaryMin: number;
  maxImaginaryMax: number;
  rsAt10kHzMin: number;
  rsAt10kHzMax: number;
  rsDriftLimit: number;
  classificationThreshold: number;
  thresholdNyquistArea: number;
  thresholdFreqArea: number;
  thresholdAscendingArcArea: number;
  thresholdMaxImaginaryInterpolated: number;
  thresholdMaxImaginaryAbsolute: number;
  inflectionPointSensitivity: number;
  slopeReversalSensitivity: number;
}

export interface DistributionMetrics {
  curveRadius: MetricStats;
  maxImaginary: MetricStats;
  rsAt10kHz: MetricStats;
  rsDrift: MetricStats;
  deltaSignal: MetricStats;
  deltaNyquistArea: MetricStats;
  deltaFreqArea: MetricStats;
  deltaAscendingArcArea: MetricStats;
  deltaMaxImaginaryInterpolated: MetricStats;
  deltaMaxImaginaryAbsolute: MetricStats;
}

export type CalibrationExclusionReason =
  | 'not_found'
  | 'measurement_incomplete'
  | 'no_baseline_data'
  | 'unreadable_data';

export interface ExcludedCalibrationTest {
  testId: string;
  sampleCode?: string | null;
  createdAt?: string | null;
  /** null when no test with this ID exists */
  status?: string | null;
  reason: CalibrationExclusionReason;
}

export interface CalibrationRecommendation {
  channelId: number;
  numTestsAnalyzed: number;
  analysisTimestamp: string;
  recommendations: ThresholdRecommendation;
  distributions: DistributionMetrics;
  /** Distinct tests sent for analysis */
  numTestsRequested: number;
  /** Requested tests left out of the analysis, each with the reason */
  excludedTests: ExcludedCalibrationTest[];
}

/** One test's metrics on one channel; drift and Δ metrics are null without a usable specimen sweep */
export interface ChannelMetricValues {
  channelId: number;
  curveRadius: number;
  maxImaginary: number;
  rsAt10kHz: number;
  rsDrift: number | null;
  deltaSignal: number | null;
  deltaNyquistArea: number | null;
  deltaFreqArea: number | null;
  deltaAscendingArcArea: number | null;
  deltaMaxImaginaryInterpolated: number | null;
  deltaMaxImaginaryAbsolute: number | null;
}

export interface TestMetricsRow {
  testId: string;
  sampleCode?: string | null;
  createdAt: string;
  status: string;
  channels: ChannelMetricValues[];
}

export interface ChannelMetricsSummary {
  channelId: number;
  numTests: number;
  distributions: DistributionMetrics;
}

/** Metrics for every channel of the requested tests — no threshold recommendations */
export interface MetricsReport {
  analysisTimestamp: string;
  numTestsRequested: number;
  /** Tests that contributed values to at least one channel */
  numTestsAnalyzed: number;
  excludedTests: ExcludedCalibrationTest[];
  channels: ChannelMetricsSummary[];
  tests: TestMetricsRow[];
}

export interface CalibrationResponse {
  recommendation: CalibrationRecommendation;
  message: string;
}

export interface SaveConfigurationRequest {
  channelId: number;
  classificationThreshold: number;
  curveRadiusMin: number;
  curveRadiusMax: number;
  maxImaginaryMin: number;
  maxImaginaryMax: number;
  rsDriftLimit: number;
  inflectionPointSensitivity: number;
  slopeReversalSensitivity: number;
  hookDetectionTolerance: number;
  signalExtractionMethod: string;
  thresholdNyquistArea: number;
  thresholdFreqArea: number;
  thresholdAscendingArcArea: number;
  thresholdMaxImaginaryInterpolated: number;
  thresholdMaxImaginaryAbsolute: number;
  lowerThresholdMaxImaginary: number;
  lowerThresholdNyquistArea: number;
  lowerThresholdFreqArea: number;
  lowerThresholdAscendingArcArea: number;
  lowerThresholdMaxImaginaryInterpolated: number;
  lowerThresholdMaxImaginaryAbsolute: number;
  notes?: string;
  disabledAnomalyChecks: string[];
}

export interface ChannelConfigurationDto {
  id: number;
  channelId: number;
  classificationThreshold: number;
  curveRadiusMin: number;
  curveRadiusMax: number;
  maxImaginaryMin: number;
  maxImaginaryMax: number;
  rsDriftLimit: number;
  inflectionPointSensitivity: number;
  slopeReversalSensitivity: number;
  hookDetectionTolerance: number;
  signalExtractionMethod: string;
  thresholdNyquistArea: number;
  thresholdFreqArea: number;
  thresholdAscendingArcArea: number;
  thresholdMaxImaginaryInterpolated: number;
  thresholdMaxImaginaryAbsolute: number;
  lowerThresholdMaxImaginary: number;
  lowerThresholdNyquistArea: number;
  lowerThresholdFreqArea: number;
  lowerThresholdAscendingArcArea: number;
  lowerThresholdMaxImaginaryInterpolated: number;
  lowerThresholdMaxImaginaryAbsolute: number;
  lastCalibratedAt?: string;
  calibratedBy?: string;
  notes?: string;
  disabledAnomalyChecks: string[];
}

export const SIGNAL_METHODS = ['MaxImaginary', 'NyquistArea', 'FreqArea', 'AscendingArcArea', 'MaxImaginaryInterpolated', 'MaxImaginaryAbsolute'] as const;
export type SignalMethod = typeof SIGNAL_METHODS[number];

export const SIGNAL_METHOD_LABELS: Record<string, string> = {
  MaxImaginary: 'Max Imaginary',
  NyquistArea: 'Nyquist Area',
  FreqArea: 'Frequency Area',
  AscendingArcArea: 'Ascending Arc Area',
  MaxImaginaryInterpolated: 'Max Imaginary Interpolated',
  MaxImaginaryAbsolute: 'Max Imaginary Absolute',
};

export const SIGNAL_METHOD_UNITS: Record<string, string> = {
  MaxImaginary: '%',
  NyquistArea: '%',
  FreqArea: '%',
  AscendingArcArea: '%',
  MaxImaginaryInterpolated: '%',
  MaxImaginaryAbsolute: 'Ω',
};

export function activeThresholdKey(method: string): keyof ChannelConfigurationDto {
  switch (method) {
    case 'NyquistArea': return 'thresholdNyquistArea';
    case 'FreqArea': return 'thresholdFreqArea';
    case 'AscendingArcArea': return 'thresholdAscendingArcArea';
    case 'MaxImaginaryInterpolated': return 'thresholdMaxImaginaryInterpolated';
    case 'MaxImaginaryAbsolute': return 'thresholdMaxImaginaryAbsolute';
    default: return 'classificationThreshold';
  }
}

export function activeLowerThresholdKey(method: string): keyof ChannelConfigurationDto {
  switch (method) {
    case 'NyquistArea': return 'lowerThresholdNyquistArea';
    case 'FreqArea': return 'lowerThresholdFreqArea';
    case 'AscendingArcArea': return 'lowerThresholdAscendingArcArea';
    case 'MaxImaginaryInterpolated': return 'lowerThresholdMaxImaginaryInterpolated';
    case 'MaxImaginaryAbsolute': return 'lowerThresholdMaxImaginaryAbsolute';
    default: return 'lowerThresholdMaxImaginary';
  }
}

export interface SaveConfigurationResponse {
  message: string;
  configuration: ChannelConfigurationDto;
}

export const classificationService = {
  async calibrate(request: CalibrationRequest): Promise<CalibrationResponse> {
    const response = await apiClient.post<CalibrationResponse>(
      '/api/classification/calibrate',
      { testIds: request.testIds, channelId: request.channelId },
    );
    return response.data;
  },

  async metricsReport(testIds: string[]): Promise<MetricsReport> {
    const response = await apiClient.post<MetricsReport>('/api/classification/metrics-report', { testIds });
    return response.data;
  },

  async getConfiguration(channelId: number): Promise<ChannelConfigurationDto> {
    const response = await apiClient.get<ChannelConfigurationDto>(
      `/api/classification/configuration/${channelId}`,
    );
    const data = response.data;
    return { ...data, disabledAnomalyChecks: data.disabledAnomalyChecks ?? [] };
  },

  async saveConfiguration(request: SaveConfigurationRequest): Promise<SaveConfigurationResponse> {
    const response = await apiClient.put<SaveConfigurationResponse>(
      '/api/classification/configuration',
      request,
    );
    const data = response.data;
    return {
      ...data,
      configuration: { ...data.configuration, disabledAnomalyChecks: data.configuration?.disabledAnomalyChecks ?? [] },
    };
  },
};
